import { ButtonUI } from '@next-order/next-order-components';
import getImage from 'helpers/getImage';
import { getLineQuantity } from 'helpers/bogoVoucherHelper';
import './BogoVoucherCartGroup.css';

const PRICE_BADGE_CLASS =
  'bogoCartGroup__pill inline-flex h-5.5 items-center justify-center rounded-lg bg-gray-50 px-1.5 py-0.5 text-3.375 font-medium leading-none text-primary-title shadow-modifier-badge sm:text-3.25';

// Splits each voucher cart line into a "buy" row and a "get" row, so a line
// whose units were consumed in both roles (e.g. 2 pizzas, buy 1 get 1) shows
// as two rows.
const buildRows = (voucherItems, getLocalItemPrice) => {
  const buyRows = [];
  const getRows = [];
  voucherItems.forEach((item) => {
    const unitPrice = Number(getLocalItemPrice(item)) / getLineQuantity(item);
    const getQuantity = Number(item.voucherGetQuantity) || 0;
    const buyQuantity = Math.max(
      (Number(item.voucherQuantity) || 0) - getQuantity,
      0
    );
    if (buyQuantity > 0) {
      buyRows.push({
        key: `buy-${item.orderIndex}`,
        item,
        quantity: buyQuantity,
        price: unitPrice * buyQuantity,
      });
    }
    if (getQuantity > 0) {
      const price = unitPrice * getQuantity;
      getRows.push({
        key: `get-${item.orderIndex}`,
        item,
        quantity: getQuantity,
        price,
        payable: Math.max(price - (Number(item.voucherGetDiscount) || 0), 0),
        isReward: true,
      });
    }
  });
  return [...buyRows, ...getRows];
};

export default function BogoVoucherCartGroup({
  title,
  voucherItems,
  voucherDiscount,
  currency,
  getLocalItemPrice,
  getItemDescription,
  onRemove,
  onChangeItem,
}) {
  const rows = buildRows(voucherItems, getLocalItemPrice);

  return (
    <div className='bogoCartGroup'>
      <div className='bogoCartGroup__header'>
        <div className='bogoCartGroup__heading'>
          <div className='bogoCartGroup__title'>{title}</div>
          {Number(voucherDiscount) > 0 && (
            <div className='bogoCartGroup__saving'>
              Saving {currency}
              {Number(voucherDiscount).toFixed(2)}
            </div>
          )}
        </div>
        <ButtonUI
          extendedClassName='!border-0 shadow-modifier-badge shrink-0'
          fontSize='sm'
          rounded='full'
          variant='outlined'
          onClick={onRemove}
        >
          Remove
        </ButtonUI>
      </div>

      {rows.map((row) => {
        const { item } = row;
        const imageUrl = getImage(item.mediaPath || item.urlS3);
        const description = getItemDescription(item) || item.categoryName;
        const isFree = row.isReward && row.payable <= 0.005;
        return (
          <div key={row.key} className='bogoCartGroup__row'>
            <span className='bogoCartGroup__image'>
              {imageUrl && <img src={imageUrl} alt='' />}
            </span>
            <span className='bogoCartGroup__body'>
              <span className='bogoCartGroup__name'>
                {row.quantity > 1 ? `${row.quantity} × ` : ''}
                {item.name}
              </span>
              <span className='bogoCartGroup__meta'>
                {row.isReward ? (
                  <>
                    <span
                      className={`${PRICE_BADGE_CLASS} bogoCartGroup__pill--reward`}
                    >
                      {isFree
                        ? 'Free'
                        : `${currency}${row.payable.toFixed(2)}`}
                    </span>
                    <span className='bogoCartGroup__strike'>
                      {currency}
                      {row.price.toFixed(2)}
                    </span>
                  </>
                ) : (
                  <span className={PRICE_BADGE_CLASS}>
                    {currency}
                    {row.price.toFixed(2)}
                  </span>
                )}
                {description && (
                  <span className='bogoCartGroup__desc'>{description}</span>
                )}
              </span>
            </span>
            {onChangeItem && (
              <button
                type='button'
                className='bogoCartGroup__action'
                onClick={() => onChangeItem(row.key)}
              >
                Change
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
